/**
 * Advanced mode — arriving with a design kept from last time: continue it, or
 * start new (which clears it). Shown over the designer, which already has the
 * kept design open behind it; it must be answered by its buttons.
 */

import React from 'react';
import { History, LayoutGrid } from 'lucide-react';
import { DesignerModal } from '../chrome/DesignerModal';
import { formatFeetInches } from '../../../utils/dimensions';
import { DESIGNER_STRINGS as S } from '../strings';
import { AdvancedState, blueprintName } from './advancedStorage';

const R = S.resume;

/** "2 hours ago", "yesterday", "on Oct 3". */
function changedWhen(at: number, now = Date.now()): string {
  const min = Math.floor((now - at) / 60000);
  if (min < 1) return R.justNow;
  if (min < 60) return R.minutes(min);
  const hr = Math.floor(min / 60);
  if (hr < 24) return R.hours(hr);
  const days = Math.floor(hr / 24);
  if (days === 1) return R.yesterday;
  if (days < 7) return R.days(days);
  return R.on(new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
}

export interface ResumePromptProps {
  state: AdvancedState;
  onContinue: () => void;
  onStartNew: () => void;
}

export const ResumePrompt: React.FC<ResumePromptProps> = ({ state, onContinue, onStartNew }) => {
  // The design shown: the one that was open, else the latest with a room.
  const withRooms = state.blueprints.filter((b) => b.plan.rooms.length > 0);
  const active = withRooms.find((b) => b.id === state.activeId);
  const shown = active ?? [...withRooms].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  if (!shown) return null;
  const room = shown.plan.rooms[0];
  const more = withRooms.reduce((n, b) => n + b.plan.rooms.length, 0) - 1;
  const latest = Math.max(...withRooms.map((b) => b.updatedAt));

  return (
    <DesignerModal title={R.title} dismissable={false} width={400} icon={<History size={17} color="#FEB100" />}>
      <p>{R.intro}</p>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', margin: '4px 0 12px',
          borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <div
          aria-hidden
          style={{
            flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 8,
            background: '#1d4b8f', backgroundImage: 'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
            backgroundSize: '8px 8px', border: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <LayoutGrid size={18} color="#fff" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {blueprintName(shown)}
          </div>
          <div style={{ color: '#c9cbd3', fontSize: 12 }}>
            {formatFeetInches(room.length)} × {formatFeetInches(room.width)}
            {more > 0 && <> · {R.moreRooms(more)}</>}
          </div>
          <div style={{ color: '#8d909b', fontSize: 11.5, marginTop: 1 }}>{R.changed(changedWhen(latest))}</div>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 12, color: '#8d909b' }}>{R.startNewNote}</p>
      <div className="dh-modal-actions">
        <button type="button" className="dh-btn" onClick={onStartNew}>{R.startNew}</button>
        <button type="button" className="dh-btn is-accent" autoFocus onClick={onContinue}>{R.continue}</button>
      </div>
    </DesignerModal>
  );
};

export default ResumePrompt;
